/**
 * AccountDeletionModal Component
 * 계정 탈퇴 요청 모달 - VROONG 브랜드 통일 디자인
 */

import React, { useState, useEffect } from "react";
import styles from '../styles/AccountDeletionModal.module.css';

interface AccountDeletionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isSubmitting: boolean;
    remainingDeposit?: number;
    hasDailyDeductions?: boolean;
    hasWeeklyPerformance?: boolean;
}

const AccountDeletionModal: React.FC<AccountDeletionModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isSubmitting,
    remainingDeposit = 0,
    hasDailyDeductions = false,
    hasWeeklyPerformance = false
}) => {
    const [isConfirmed, setIsConfirmed] = useState(false);

    // 모달이 닫힐 때 체크박스 상태 초기화
    useEffect(() => {
        if (!isOpen) {
            setIsConfirmed(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    // 탈퇴 가능 여부 확인
    const canDelete = remainingDeposit >= 0 && !hasDailyDeductions && !hasWeeklyPerformance;

    return (
        <div className={styles['modal-overlay']} onClick={onClose}>
            <div className={styles['modal-container']} onClick={(e) => e.stopPropagation()}>
                {/* 모달 헤더 */}
                <div className={styles['modal-header']}>
                    <h2 className={styles['modal-title']}>
                        <span className={styles['modal-title-icon']}>⚠️</span>
                        계정 탈퇴 요청
                    </h2>
                    <button className={styles['close-btn']} onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* 모달 컨텐츠 */}
                <div className={styles['modal-content']}>
                    {/* 앱 정보 */}
                    <section className={styles['modal-section']}>
                        <div className={styles['app-info-box']}>
                            <h3 className={styles['section-subtitle']}>
                                <span className={styles['section-subtitle-icon']}>📱</span>
                                앱 정보
                            </h3>
                            <div className={styles['info-row']}>
                                <span className={styles['info-label']}>앱 이름</span>
                                <span className={styles['info-value']}>VROONG 3PL Platform</span>
                            </div>
                        </div>
                    </section>

                    {/* 중요 안내 */}
                    <section className={styles['modal-section']}>
                        <div className={styles['important-notice']}>
                            <i className={`fas fa-exclamation-triangle ${styles['warning-icon']}`}></i>
                            <div className={styles['notice-content']}>
                                <h3 className={styles['notice-title']}>중요 안내</h3>
                                <p className={styles['notice-text']}>
                                    라이더는 임의로 계정을 삭제할 수 없습니다.
                                    계정 탈퇴는 계약 종료 절차의 일부이므로,
                                    반드시 지점장에게 문의해야 합니다.
                                </p>

                                <div className={styles['condition-check-list']}>
                                    <div className={styles['condition-item']}>
                                        <span className={styles['condition-label']}>잔여 예치금</span>
                                        <span className={`${styles['condition-value']} ${remainingDeposit < 0 ? styles['negative'] : ''}`}>
                                            {remainingDeposit.toLocaleString()}원
                                        </span>
                                    </div>
                                    {remainingDeposit > 0 && (
                                        <div className={styles['deposit-warning']}>
                                            <i className="fas fa-exclamation-circle"></i>
                                            <p>
                                                <strong>탈퇴 요청 시 잔여 예치금은 자동으로 소멸 처리됩니다.</strong><br />
                                                탈퇴 진행 시 위 금액에 대한 반환 요청을 포기하는 것으로 간주됩니다.
                                            </p>
                                        </div>
                                    )}
                                    <div className={styles['condition-item']}>
                                        <span className={styles['condition-label']}>잔여 일차감 내역</span>
                                        <span className={styles['condition-status']}>
                                            {hasDailyDeductions ? '있음' : '없음'}
                                        </span>
                                    </div>
                                    <div className={styles['condition-item']}>
                                        <span className={styles['condition-label']}>수~화 중 수행내역</span>
                                        <span className={styles['condition-status']}>
                                            {hasWeeklyPerformance ? '주정산에 정산예정' : '없음'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* 탈퇴 절차 */}
                    <section className={styles['modal-section']}>
                        <h3 className={styles['section-subtitle']}>
                            <span className={styles['section-subtitle-icon']}>📝</span>
                            탈퇴 절차
                        </h3>
                        <div className={styles['steps-list']}>
                            <div className={styles['step-item']}>
                                <div className={styles['step-number']}>1</div>
                                <div className={styles['step-text']}>
                                    <strong>지점장에게 계약 종료 문의</strong>
                                    <p>소속 지점의 지점장에게 계약 종료 의사를 전달하고 상담을 진행합니다.</p>
                                </div>
                            </div>
                            <div className={styles['step-item']}>
                                <div className={styles['step-number']}>2</div>
                                <div className={styles['step-text']}>
                                    <strong>계약 종료 절차 진행</strong>
                                    <p>지점장의 안내에 따라 정산 완료, 장비 반납 등 계약 종료에 필요한 절차를 진행합니다.</p>
                                </div>
                            </div>
                            <div className={styles['step-item']}>
                                <div className={styles['step-number']}>3</div>
                                <div className={styles['step-text']}>
                                    <strong>계정 삭제 처리</strong>
                                    <p>계약 종료가 완료되면, 관리자가 계정 삭제를 처리하고 관련 데이터가 삭제됩니다.</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* 데이터 처리 안내 */}
                    <section className={styles['modal-section']}>
                        <h3 className={styles['section-subtitle']}>
                            <span className={styles['section-subtitle-icon']}>💾</span>
                            데이터 처리 안내
                        </h3>

                        <div className={`${styles['data-box']} ${styles['deleted-data']}`}>
                            <h4 className={styles['data-title']}>
                                <i className="fas fa-trash-alt"></i>
                                즉시 삭제되는 데이터
                            </h4>
                            <ul className={styles['data-list']}>
                                <li>로그인 정보 (아이디, 비밀번호)</li>
                                <li>개인 프로필 정보 (이름, 연락처, 생년월일)</li>
                                <li>계좌 정보</li>
                                <li>앱 사용 내역 및 설정</li>
                            </ul>
                        </div>

                        <div className={`${styles['data-box']} ${styles['retained-data']}`}>
                            <h4 className={styles['data-title']}>
                                <i className="fas fa-archive"></i>
                                일정 기간 보관되는 데이터
                            </h4>
                            <ul className={styles['data-list']}>
                                <li>
                                    <strong>배달 이력 및 정산 기록</strong>
                                    <span className={styles['retention-tag']}>5년 보관</span>
                                    <p className={styles['data-desc']}>세법에 따라 세무 신고 및 정산 관련 법적 의무 준수를 위해 보관됩니다.</p>
                                </li>
                                <li>
                                    <strong>출금 및 입금 내역</strong>
                                    <span className={styles['retention-tag']}>5년 보관</span>
                                    <p className={styles['data-desc']}>전자금융거래법에 따라 금융 거래 기록은 법령에 따라 의무적으로 보관됩니다.</p>
                                </li>
                                <li>
                                    <strong>계약 관련 서류</strong>
                                    <span className={styles['retention-tag']}>3년 보관</span>
                                    <p className={styles['data-desc']}>근로기준법에 따라 계약서, 근로 관련 서류는 법적 보존 의무가 있습니다.</p>
                                </li>
                            </ul>
                        </div>

                        <div className={styles['retention-info']}>
                            <i className="fas fa-info-circle"></i>
                            <p>
                                보관 기간이 만료된 데이터는 관련 법령에 따라 안전하게 파기됩니다.
                                보관 기간 중에는 법적 요구사항 준수를 위해서만 사용되며,
                                그 외의 목적으로는 사용되지 않습니다.
                            </p>
                        </div>
                    </section>

                    {/* 탈퇴 요청 확인 */}
                    <section className={styles['modal-section']}>
                        <div className={styles['confirmation-box']}>
                            <h3 className={styles['section-subtitle']}>
                                <span className={styles['section-subtitle-icon']}>✅</span>
                                탈퇴 요청 확인
                            </h3>
                            <p className={styles['confirmation-text']}>
                                위 내용을 모두 확인하셨습니까?<br />
                                탈퇴 버튼을 누르면 지점장에게 탈퇴 요청이 전달됩니다.
                            </p>
                            <div className={styles['checkbox-container']}>
                                <label className={styles['checkbox-label']}>
                                    <input
                                        type="checkbox"
                                        className={styles['checkbox-input']}
                                        checked={isConfirmed}
                                        onChange={(e) => setIsConfirmed(e.target.checked)}
                                        disabled={!canDelete}
                                    />
                                    <span>위 내용을 모두 확인했으며, 탈퇴 절차를 진행하겠습니다.</span>
                                </label>
                            </div>
                        </div>
                    </section>
                </div>

                {/* 모달 푸터 */}
                <div className={styles['modal-footer']}>
                    <button
                        className={`${styles['modal-btn']} ${styles['cancel-btn']}`}
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        취소
                    </button>
                    <button
                        className={`${styles['modal-btn']} ${styles['delete-btn']}`}
                        onClick={onConfirm}
                        disabled={isSubmitting || !canDelete || !isConfirmed}
                    >
                        {isSubmitting
                            ? '처리 중...'
                            : remainingDeposit < 0
                                ? '탈퇴 불가 (미납금 존재)'
                                : hasWeeklyPerformance
                                    ? '탈퇴 불가 (정산 대상)'
                                    : hasDailyDeductions
                                        ? '탈퇴 불가 (일차감 존재)'
                                        : !isConfirmed
                                            ? '확인 필요'
                                            : '탈퇴'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default AccountDeletionModal;
